// ---------------------------------------------------------------------------
// useOverviewHighlights
//
// Thin wrapper around `useApi` that fetches the Overview tab's highlights
// envelope (`GET /api/management-view/overview/highlights`) for the active
// scope/window/cadence filters. Refetches whenever any filter value changes.
//
// The hook does NOT serialise the filters into URL params itself — that lives
// in `management-view-overview-api`. We only thread the values through
// `useApi`'s dependency array so a new request fires on each filter change.
//
// When filters change while a request is in flight, the latest request wins.
// `useApi` captures the fetcher closure on each dependency change and the
// rendered state always reflects the most recent `load()` call's result, so
// prior in-flight responses are effectively discarded by React's render
// scheduling. (Mirrors Phase 1 US5's stale-result discard pattern in
// `use-quality-control-matrices.ts`.)
// ---------------------------------------------------------------------------

import { useApi, type UseApiResult } from './useApi';
import { managementViewOverviewApi } from '@/services/management-view-overview-api';
// scopeDepKey lives in the shared query module (S7721-safe module scope).
import { scopeDepKey, type IManagementViewQuery } from '@/lib/management-view-query';
import type { IOverviewHighlightsResponse } from '@skystone/shared';

export type IUseOverviewHighlightsArgs = IManagementViewQuery;

export function useOverviewHighlights(
  args: IUseOverviewHighlightsArgs,
): UseApiResult<IOverviewHighlightsResponse> {
  const { scope, window, cadence, includeArchived } = args;
  return useApi<IOverviewHighlightsResponse>(
    () => managementViewOverviewApi.getHighlights({ scope, window, cadence, includeArchived }),
    [scopeDepKey(scope), window, cadence, includeArchived === true],
  );
}
